import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground',
        outline: 'text-foreground',
        /*
         * Semantic variants resolve through the status tones (F-FE-N55), not
         * raw palette classes. `bg-yellow-500 text-white` in particular was a
         * 2.1:1 contrast failure in every theme; the warning tone carries dark
         * ink on amber instead.
         */
        success:
          'border-transparent bg-success-solid text-success-solid-fg',
        warning:
          'border-transparent bg-warning-solid text-warning-solid-fg',
        danger:
          'border-transparent bg-danger-solid text-danger-solid-fg',
        info:
          'border-transparent bg-info-solid text-info-solid-fg',
        /* Tinted counterparts — the common "status chip" shape. */
        'success-surface': 'bg-success-surface text-success-fg border-success-border',
        'warning-surface': 'bg-warning-surface text-warning-fg border-warning-border',
        'danger-surface': 'bg-danger-surface text-danger-fg border-danger-border',
        'info-surface': 'bg-info-surface text-info-fg border-info-border',
        'neutral-surface': 'bg-neutral-surface text-neutral-fg border-neutral-border',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }

